/**
 * Shared lightweight-charts v5 plumbing: themed base options, epoch-ms → chart
 * time conversion, and a lifecycle hook that creates/resizes/disposes a chart
 * and re-runs a memoized render callback when theme, size, or data change.
 *
 * lightweight-charts draws to a `<canvas>`, so it takes explicit hex colors from
 * `lib/palette.ts` rather than the CSS tokens the DOM primitives use.
 */
import { useEffect, useRef } from "react";
import {
  ColorType,
  CrosshairMode,
  LineStyle,
  TickMarkType,
  createChart,
  isUTCTimestamp,
  type ChartOptions,
  type DeepPartial,
  type IChartApi,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import { VIZ_FONT_FAMILY, paletteFor, type VizPalette } from "../lib/palette";
import type { Theme } from "../lib/theme";

/** Epoch milliseconds (contracts `EpochMs`) → lightweight-charts UTC seconds. */
export function toUtcTime(epochMs: number): UTCTimestamp {
  return Math.floor(epochMs / 1000) as UTCTimestamp;
}

/**
 * lightweight-charts requires strictly ascending, de-duplicated time points.
 * Sort by time and keep the last sample per timestamp.
 */
export function normalizeByTime<T extends { time: UTCTimestamp }>(points: T[]): T[] {
  const sorted = [...points].sort((a, b) => (a.time as number) - (b.time as number));
  const out: T[] = [];
  for (const p of sorted) {
    const last = out[out.length - 1];
    if (last && last.time === p.time) {
      out[out.length - 1] = p;
    } else {
      out.push(p);
    }
  }
  return out;
}

/**
 * lightweight-charts stores every point as UTC seconds and formats axis/crosshair
 * time in UTC by default — a Seoul viewer would see a 14:30 KST snapshot labeled
 * "05:30". Both formatters below convert back through `Date`, which renders in
 * the browser's local zone (KST for the operator), with no explicit `timeZone`.
 */
function toLocalDate(time: Time): Date | null {
  return isUTCTimestamp(time) ? new Date(time * 1000) : null;
}

/** Crosshair time-of-day label (`localization.timeFormatter`). */
function timeFormatter(time: Time): string {
  const date = toLocalDate(time);
  if (!date) return String(time);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Axis tick labels (`timeScale.tickMarkFormatter`). Mirrors the library's default
 * granularity per `tickMarkType` — just in local wall-clock time instead of UTC.
 */
function tickMarkFormatter(time: Time, tickMarkType: TickMarkType, locale: string): string | null {
  const date = toLocalDate(time);
  if (!date) return null;
  switch (tickMarkType) {
    case TickMarkType.Year:
      return date.toLocaleDateString(locale, { year: "numeric" });
    case TickMarkType.Month:
      return date.toLocaleDateString(locale, { month: "short" });
    case TickMarkType.DayOfMonth:
      return date.toLocaleDateString(locale, { day: "numeric" });
    case TickMarkType.Time:
      return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
    case TickMarkType.TimeWithSeconds:
      return date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    default:
      return null;
  }
}

/**
 * One level of plain-object recursion, `undefined`-skipping — enough for
 * `DeepPartial<ChartOptions>`, which nests options objects but never arrays.
 * Callers passing their own `localization`/`timeScale` (e.g. a `priceFormatter`)
 * merge alongside the base's `timeFormatter`/`tickMarkFormatter` instead of
 * replacing them.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function mergeChartOptions<T extends Record<string, unknown>>(
  base: T,
  override: DeepPartial<T> | undefined,
): T {
  if (!override) return base;
  const merged: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (value === undefined) continue;
    const baseValue = merged[key];
    merged[key] =
      isPlainObject(baseValue) && isPlainObject(value)
        ? mergeChartOptions(baseValue, value as DeepPartial<typeof baseValue>)
        : value;
  }
  return merged as T;
}

export function buildChartOptions(
  palette: VizPalette,
  width: number,
  height: number,
): DeepPartial<ChartOptions> {
  return {
    width,
    height,
    autoSize: false,
    layout: {
      background: { type: ColorType.Solid, color: "transparent" },
      textColor: palette.inkSecondary,
      fontFamily: VIZ_FONT_FAMILY,
      fontSize: 11,
      attributionLogo: false,
    },
    grid: {
      vertLines: { visible: false, color: palette.grid },
      horzLines: { color: palette.grid, style: LineStyle.Solid },
    },
    rightPriceScale: {
      borderVisible: false,
      borderColor: palette.axis,
      scaleMargins: { top: 0.12, bottom: 0.1 },
    },
    localization: { timeFormatter },
    timeScale: {
      borderVisible: false,
      borderColor: palette.axis,
      timeVisible: true,
      secondsVisible: false,
      fixLeftEdge: true,
      fixRightEdge: true,
      tickMarkFormatter,
    },
    crosshair: {
      mode: CrosshairMode.Normal,
      vertLine: {
        width: 1,
        color: palette.muted,
        style: LineStyle.Dashed,
        labelBackgroundColor: palette.inkSecondary,
      },
      horzLine: {
        width: 1,
        color: palette.muted,
        style: LineStyle.Dashed,
        labelBackgroundColor: palette.inkSecondary,
      },
    },
    handleScale: { axisPressedMouseMove: { time: true, price: false } },
  };
}

export interface UseLightweightChartParams {
  theme: Theme;
  height: number;
  /**
   * Attach series + data to the chart. MUST be memoized (`useCallback`) with the
   * component's data deps — the chart is rebuilt whenever this identity changes.
   */
  render: (chart: IChartApi, palette: VizPalette) => void;
  /** Extra top-level option overrides merged over the themed base. */
  options?: DeepPartial<ChartOptions>;
}

/**
 * Own a lightweight-charts instance for the lifetime of a container element.
 * Rebuilds on theme/height/render change; keeps width in sync via ResizeObserver.
 */
export function useLightweightChart({
  theme,
  height,
  render,
  options,
}: UseLightweightChartParams): { containerRef: React.RefObject<HTMLDivElement | null> } {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const palette = paletteFor(theme);
    const width = el.clientWidth || 600;
    const chart = createChart(
      el,
      mergeChartOptions(buildChartOptions(palette, width, height), options),
    );

    render(chart, palette);
    chart.timeScale().fitContent();

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const nextWidth = entry ? entry.contentRect.width : el.clientWidth;
      if (nextWidth > 0) {
        chart.applyOptions({ width: Math.floor(nextWidth), height });
        chart.timeScale().fitContent();
      }
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      chart.remove();
    };
  }, [theme, height, render, options]);

  return { containerRef };
}
